/** Fences rendered as platform widgets, never as authored executable examples. */
export const READING_WIDGET_LANGUAGES = new Set([
    'reading-sections',
    'collabboard-lab',
    'study-aid',
    'learning-steps',
    'learning-lab',
    'learning-hint',
    'mermaid',
]);
/** Hide authoring syntax without changing any canonical source line identity. */
export function readingRenderSource(source) {
    return source
        .replace(/^```reading-sections\s*\r?\n[\s\S]*?\r?\n```[^\S\r\n]*(?:\r?\n|$)/, (metadata) => metadata.replace(/[^\r\n]/g, ''))
        .replace(/^<!-- section: [a-z0-9-]+ -->[^\S\r\n]*$/gm, '');
}
/** Plain Markdown export: preserve examples and expand optional platform hints. */
export function readingExportSource(source) {
    let result = readingRenderSource(source).trim();
    for (const fence of readingSourceIndex(result).fences.reverse()) {
        if (fence.language !== 'learning-hint')
            continue;
        result =
            result.slice(0, fence.start) +
                `**Optional help**\n\n${readingExportSource(fence.code)}\n\n` +
                result.slice(fence.end);
    }
    return result.trim();
}
/** Canonical source positions shared by indexing, rendering and focused reads. */
export function readingSourceIndex(source) {
    const lines = [...source.matchAll(/([^\r\n]*)(\r\n|\r|\n|$)/g)]
        .filter((match) => match[0])
        .map((match, index) => ({
        text: match[1] ?? '',
        start: match.index,
        end: match.index + match[0].length,
        line: index + 1,
    }));
    const headings = [];
    const fences = [];
    for (let index = 0; index < lines.length; index++) {
        const line = lines[index];
        if (!line)
            continue;
        const opening = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line.text);
        const marker = opening?.[1];
        const info = opening?.[2]?.trim() ?? '';
        if (marker && !(marker.startsWith('`') && info.includes('`'))) {
            while (++index < lines.length) {
                const closing = lines[index];
                if (!closing)
                    continue;
                const end = /^ {0,3}(`{3,}|~{3,})[ \t]*$/.exec(closing.text)?.[1];
                if (!end?.startsWith(marker[0] ?? '`') || end.length < marker.length)
                    continue;
                fences.push({
                    id: `block-L${line.line}`,
                    language: /^\S+/.exec(info)?.[0]?.toLowerCase() ?? 'plaintext',
                    info,
                    code: source.slice(line.end, closing.start),
                    sourceLine: line.line,
                    lineCount: index - line.line,
                    start: line.start,
                    end: closing.end,
                });
                break;
            }
            continue;
        }
        const heading = /^ {0,3}(#{1,6})\s+(.+?)(?:\s+#+)?\s*$/.exec(line.text);
        if (heading?.[1] && heading[2])
            headings.push({
                title: heading[2].replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/[`*_]/g, ''),
                level: heading[1].length,
                sourceLine: line.line,
                start: line.start,
            });
    }
    return { headings, fences };
}
